<template>
  <div class="home-container">
    <section id="banner" class="relative">
      <v-img
        :src="banner_image"
        :lazy-src="require('../assets/banner.jpg')"
        alt="Happy Harbour Celebration Cafe Interior"
        class="banner-image align-center"
        gradient="to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0)"
      >
        <v-container>
          <v-row class="banner-container-row">
            <v-col cols="auto">
              <v-card
                component="article"
                class="rounded-xl overflow-hidden glass-card mx-auto"
                elevation="24"
              >
                <v-sheet height="12" class="deep-purple accent-3"></v-sheet>
                <div class="pa-4 pa-sm-6 pa-md-10 white">
                  <header class="text-center mb-4">
                    <h1
                      class="text-h4 font-weight-bold grey--text text--darken-3 mb-1"
                    >
                      Happy Harbour
                    </h1>
                    <div
                      class="subtitle-1 deep-purple--text text--darken-1 font-weight-medium"
                    >
                      The Celebrations Cafe
                    </div>
                  </header>

                  <div class="mb-2" role="group" aria-label="Select Branch">
                    <div
                      class="text-center text-overline grey--text text--darken-1 mb-2 ml-1"
                      id="branch-label"
                    >
                      Select Location
                    </div>
                    <v-slide-group
                      v-model="form.branch"
                      class="py-1 text-center"
                      retur-object
                      center-active
                      aria-labelledby="branch-label"
                    >
                      <v-slide-item
                        v-for="location in locations"
                        :key="location.id"
                        v-slot="{ active, toggle }"
                        :value="location.id"
                      >
                        <v-chip
                          class="ma-1 px-6 py-5 font-weight-medium transition-swing"
                          :color="
                            active ? 'deep-purple accent-3' : 'grey lighten-4'
                          "
                          :text-color="active ? 'white' : 'grey darken-1'"
                          :elevation="active ? 4 : 0"
                          large
                          @click="toggle"
                          role="radio"
                          :aria-checked="active"
                        >
                          {{ location.name }}
                        </v-chip>
                      </v-slide-item>
                    </v-slide-group>
                  </div>

                  <v-form
                    ref="formRef"
                    v-model="valid"
                    @submit.prevent="submit"
                  >
                    <v-menu
                      v-model="menu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.date"
                          label="When are we celebrating?"
                          placeholder="Select a date"
                          prepend-inner-icon="mdi-calendar-heart"
                          readonly
                          solo
                          flat
                          background-color="grey lighten-4"
                          class="rounded-lg soft-input"
                          height="56"
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                          :rules="[r.required]"
                          aria-label="Select celebration date"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.date"
                        :min="today"
                        color="deep-purple accent-3"
                        no-title
                        scrollable
                        @input="menu = false"
                      ></v-date-picker>
                    </v-menu>

                    <v-btn
                      block
                      x-large
                      class="mt-4 rounded-xl white--text gradient-btn"
                      elevation="6"
                      height="64"
                      type="submit"
                      aria-label="Book Celebration Now"
                    >
                      Book Celebration
                      <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>

                    <div
                      class="text-center caption grey--text text--darken-1 mt-4"
                    >
                      <v-icon
                        x-small
                        color="green"
                        class="mr-1"
                        aria-hidden="true"
                      >
                        mdi-shield-check
                      </v-icon>
                      Instant confirmation • No hidden fees
                    </div>
                  </v-form>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-img>
    </section>

    <section
      id="about-us"
      class="py-16"
      style="background: linear-gradient(to bottom, #f3e5f5, #ffffff)"
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8" class="text-center">
            <div class="py-16">
              <h2 class="deep-purple--text mb-2">Our Story</h2>
              <h2
                class="text-h4 text-md-h3 font-weight-bold grey--text text--darken-3 mb-6"
              >
                Making Moments Special
              </h2>
              <div
                class="text-h6 font-weight-regular grey--text text--darken-1 lh-loose"
              >
                "At Happy Harbour, we're passionate about turning life's moments
                into unforgettable celebrations. With a diverse range of spaces
                and affordable luxury starting from just
                <span class="deep-purple--text font-weight-bold">₹699</span>, we
                are dedicated to making your milestones shine."
              </div>
              <v-icon
                large
                color="deep-purple lighten-4"
                class="mt-8"
                aria-hidden="true"
              >
                mdi-format-quote-close
              </v-icon>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section
      id="our-space"
      class="py-16"
      style="background: linear-gradient(to bottom, #ffffff, #f3e5f5)"
    >
      <v-container>
        <div class="text-center mb-12">
          <h2 class="text-h4 font-weight-bold deep-purple--text text--darken-2">
            Our Spaces
          </h2>
          <div class="subtitle-1 grey--text">
            Choose the perfect setting for your story
          </div>
        </div>

        <v-row justify="center">
          <v-col
            cols="11"
            sm="6"
            md="4"
            lg="3"
            v-for="(plan, index) in plans"
            :key="index"
            class="d-flex"
            component="article"
          >
            <v-card
              class="mx-auto rounded-xl flex-grow-1 space-card"
              elevation="0"
              hover
              :to="{
                name: 'SpaceDetails',
                params: {
                  location: plan.branch,
                  space: plan.title.split(' ').join('-').toLowerCase(),
                },
              }"
            >
              <v-img
                :src="plan.image"
                :alt="`Decoration setup for ${plan.title}`"
                height="300"
                class="rounded-xl ma-2 align-end"
                gradient="to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.7)"
              >
                <div class="white--text pa-4 font-weight-bold text-h5">
                  {{ plan.title }}
                </div>
              </v-img>

              <v-card-text class="pt-4 pb-0">
                <div
                  class="body-2 grey--text text--darken-2 mb-4"
                  style="line-height: 1.6"
                >
                  {{ plan.description }}
                </div>
              </v-card-text>

              <v-card-actions class="pa-4 pt-0">
                <v-btn
                  block
                  large
                  text
                  color="deep-purple accent-3"
                  class="rounded-lg font-weight-bold"
                  :to="{
                    name: 'SpaceDetails',
                    params: {
                      location: plan.branch,
                      space: plan.title.split(' ').join('-').toLowerCase(),
                    },
                  }"
                  :aria-label="`Book ${plan.title} starting at ${plan.price} rupees`"
                >
                  Book at Rs. {{ plan.price }}/-
                  <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section
      id="customer-reviews"
      class="py-16"
      style="background: linear-gradient(to bottom, #f3e5f5, #ffffff)"
    >
      <v-container>
        <div class="text-center mb-12">
          <h2 class="text-h4 font-weight-bold grey--text text--darken-3">
            Customer Love
          </h2>
          <div class="subtitle-1 grey--text">
            Stories from our happy celebrators
          </div>
        </div>

        <v-row justify="center">
          <v-col v-for="(review, i) in reviews" :key="i" cols="11" md="4">
            <v-card
              class="rounded-xl pa-6 elevation-3 white fill-height d-flex flex-column"
            >
              <div
                class="d-flex mb-4"
                :aria-label="`Rated ${review.rating} out of 5 stars`"
              >
                <v-icon v-for="n in 5" :key="n" small color="amber">
                  {{ n <= review.rating ? "mdi-star" : "mdi-star-outline" }}
                </v-icon>
              </div>

              <div
                class="body-1 grey--text text--darken-2 flex-grow-1 mb-6"
                style="line-height: 1.6"
              >
                "{{ review.text }}"
              </div>

              <div class="d-flex align-center">
                <v-avatar color="deep-purple lighten-5" size="40" class="mr-3">
                  <span class="deep-purple--text font-weight-bold">{{
                    review.initials
                  }}</span>
                </v-avatar>
                <div>
                  <div
                    class="subtitle-2 font-weight-bold grey--text text--darken-3"
                  >
                    {{ review.name }}
                  </div>
                  <div class="caption grey--text">
                    {{ review.date }}
                  </div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section
      id="visit-us"
      class="py-16 white"
      style="background: linear-gradient(to bottom, #ffffff, #f3e5f5)"
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12">
            <div class="text-center mb-12">
              <h2 class="text-h4 font-weight-bold grey--text text--darken-3">
                Visit Us
              </h2>
            </div>
          </v-col>

          <v-col cols="11" md="6" lg="8" class="mb-6 mb-md-0">
            <v-card class="rounded-xl overflow-hidden h-100" elevation="2">
              <div class="pa-4 font-weight-bold deep-purple--text">
                RR Nagar Branch
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.0747545193108!2d77.5164803108161!3d12.9029146163276!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3ff2955bfacd%3A0x43bd13707f8ef6eb!2sHappy%20Harbour%20-%20The%20Celebration%20Cafe%20%26%20Private%20Theater%20-%20RR%20Nagar!5e0!3m2!1sen!2sin!4v1768073962731!5m2!1sen!2sin"
                title="Map location of RR Nagar Branch"
                width="100%"
                height="350"
                style="border: 0; display: block"
                allowfullscreen=""
                loading="lazy"
              ></iframe>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section
      id="contact-us"
      class="py-16"
      style="background: linear-gradient(to bottom, #f3e5f5, #ffffff)"
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12">
            <v-card
              class="rounded-xl pa-6 pa-md-12 h-100 grey lighten-5 text-center"
              flat
            >
              <v-col cols="12">
                <div class="text-center mb-12">
                  <h2
                    class="text-h4 font-weight-bold grey--text text--darken-3"
                  >
                    Get In Touch
                  </h2>
                </div>
              </v-col>

              <contact-form></contact-form>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import ContactForm from "@/components/ContactForm.vue";
import { getAllLocations } from "@/services/booking/booking.service";

export default {
  name: "HomeView",
  components: {
    ContactForm,
  },

  filters: {
    formatCompact(num) {
      if (!num) return "0";
      return Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(num);
    },
  },

  data() {
    return {
      valid: false,
      menu: false,
      today: new Date().toISOString().substr(0, 10),
      banner_image: require("../assets/banner.jpg"),

      form: {
        branch: 1,
        date: new Date().toISOString().substr(0, 10),
      },
      r: {
        required: (v) => !!v || "Required",
      },
      locations: [],

      reviews: [
        {
          name: "Priya Sharma",
          initials: "PS",
          rating: 5,
          text: "The Gala Red room was absolutely magical for our anniversary! The neon lights and private theater setup were perfect. Highly recommend!",
          date: "2 days ago",
        },
        {
          name: "Rahul K",
          initials: "RK",
          rating: 5,
          text: "Great experience at the RR Nagar branch. The staff was super helpful in setting up the surprise. My wife loved it",
          date: "1 week ago",
        },
        {
          name: "Sneha M",
          initials: "SM",
          rating: 5,
          text: "Beautiful decor and very affordable pricing compared to others. The Midnight Garden theme is stunning!",
          date: "3 weeks ago",
        },
      ],

      plans: [
        {
          title: "Golden Glam",
          price: 499,
          image: require("@/assets/gallery/rr_nagar/golden_glam/1.jpg"),
          description:
            "Elevate your group celebrations. Black and gold balloons, perfect for gatherings up to 6.",
          color: "yellow darken-3",
          branch: "rr-nagar",
        },
        {
          title: "Red Love",
          price: 699,
          image: require("@/assets/gallery/rr_nagar/gala_red/1.jpg"),
          description:
            "Romantic decor adorned with red balloons, personalized neon lighting, and cozy sofa seating. Perfect for anniversaries.",
          color: "red",
          branch: "rr-nagar",
        },
        {
          title: "Lavender",
          price: 999,
          image: require("@/assets/gallery/rr_nagar/lavender/1.jpeg"),
          description:
            "Expansive setup with personalized neon lighting, fully equipped table with props, spacious seating and projector.",
          color: "deep-purple",
          branch: "rr-nagar",
        },
        {
          title: "Sapphire Suite",
          price: 1299,
          image: require("@/assets/gallery/rr_nagar/sapphire/1.jpeg"),
          description:
            "Sophisticated spacious room with 7ft rectangle decor, elegant blue & white balloons. Amenities for groups up to 8.",
          color: "blue",
          branch: "rr-nagar",
        },
      ],
    };
  },

  methods: {
    async submit() {
      if (this.$refs.formRef.validate()) {
        const location = this.locations.filter(
          (location) => location.id === this.form.branch,
        )[0];

        this.$store.commit("setSelectedLocation", location);
        await this.$nextTick();
        this.$router.push({
          name: "BookOnline",
          params: { location: location.name.toLowerCase() },
        });
      }
    },

    scrollTo(id) {
      this.$vuetify.goTo(id, { duration: 750, offset: 0 });
    },

    getAllLocations() {
      getAllLocations()
        .then((response) => {
          this.locations = response.data.data;
        })
        .catch((error) => {
          console.error("Error fetching locations:", error);
        });
    },

    // SEO: Creates Structured Data Schema
    insertSchema() {
      // Check if schema already exists to prevent duplicates
      if (document.getElementById("schema-jsonld")) return;

      const script = document.createElement("script");
      script.id = "schema-jsonld";
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Happy Harbour",
        image: this.banner_image,
        priceRange: "₹699 - ₹999",
        description:
          "Private celebration cafe for anniversaries, birthdays and special moments.",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Bengaluru",
          addressRegion: "KA",
          addressCountry: "IN",
        },
      });
      document.head.appendChild(script);
    },
  },

  mounted() {
    if (this.$route.hash) {
      this.scrollTo(this.$route.hash);
    }

    this.insertSchema(); // Inject SEO Schema on mount
    this.getAllLocations();
  },

  watch: {
    "$route.hash"(val) {
      if (val) this.scrollTo(val);
    },
  },
};
</script>

<style scoped>
/* Gradient for CTA Button */
.banner-image {
  height: calc(100vh - 60px);
}
.banner-container-row {
  justify-self: end;
}
.gradient-btn {
  background: linear-gradient(45deg, #6200ea, #9575cd);
  transition: transform 0.2s, box-shadow 0.2s;
  letter-spacing: 1px;
  font-weight: 700;
}
.gradient-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2) !important;
}

/* Enhances the 'Solo' field look */
.soft-input >>> .v-input__slot {
  padding-left: 20px !important;
}

/* Subtle separator */
.border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}

/* Chip Transition */
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.home-container >>> .v-slide-group__content {
  justify-content: center;
}

/* Typography helper */
.lh-loose {
  line-height: 1.8 !important;
}

/* Space Cards Hover Effect */
.space-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: white;
}
.space-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08) !important;
}

/* Glass effect for main banner card */
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
}
@media only screen and (max-width: 600px) {
  .banner-image {
    height: calc(100vh - 120px);
  }
  .banner-container-row {
    justify-self: center;
  }
}
</style>
